import { useQuery } from '@tanstack/react-query'
import { useState } from 'react'
import { DHL_API } from '../../api'
import {
  DamagesBySide,
  History,
  INITIAL_DAMAGE_FIELDS,
  QueryKey,
  VehicleDataResponse,
  resolveVehicleTypeSvgName,
} from '../../constants'
import { useAppStore } from '../../store/useAppStore'
import { useParamsFromURL } from '../useParamsFromURL'
import useVehicleId from '../useVehicleId'

export const useVehicleDataQuery = () => {
  const { webView: isWebView, customTab } = useParamsFromURL([
    'webView',
    'customTab',
  ])
  const vehicleId = useVehicleId()
  const vehicleData = useAppStore((state) => state.vehicleData)
  const setVehicleData = useAppStore((state) => state.setVehicleData)

  const [damages, setDamages] = useState<DamagesBySide>(INITIAL_DAMAGE_FIELDS)
  const [photos, setPhotos] = useState<History[]>([])
  const [fullPhotos, setFullPhotos] = useState<History[]>([])
  const [overviewImages, setOverviewImages] = useState(null)

  const {
    isFetching = false,
    isError,
    refetch: fetchData,
  } = useQuery<VehicleDataResponse>(
    [QueryKey.VehicleData],
    () => DHL_API.get(`api/fleet/storegear/vehicle/${vehicleId}`),
    {
      refetchOnWindowFocus: false,
      refetchOnMount: false,
      enabled: !!vehicleId && !vehicleData[0].photo,
      onSuccess: async (data) => {
        const {
          data: { history, damages: newDamages, vehicleType },
          status,
        } = data

        if (isWebView === 'true' && window.Android && status !== 200) {
          window.Android.failVehicleDamageRegistrationApp()
          customTab === 'true' && window.close()
        }

        setPhotos(history)

        const vehicleFullPhotos = (
          await DHL_API.get(`api/fleet/storegear/vehicles/photos`, {
            params: {
              vehicleIds: [vehicleId],
            },
          })
        ).data

        let overview
        // Check if overview images are already fetched
        // If not, fetch them
        // If yes, use the fetched images
        if (!overviewImages) {
          const svgType = resolveVehicleTypeSvgName(vehicleType)
          const frontOverviewImage = (
            await DHL_API.get(
              `app/fleet/img/vehicle-damage-location/${svgType}_voorkant.svg`
            )
          ).data
          const leftOverviewImage = (
            await DHL_API.get(
              `app/fleet/img/vehicle-damage-location/${svgType}_links.svg`
            )
          ).data
          const rearOverviewImage = (
            await DHL_API.get(
              `app/fleet/img/vehicle-damage-location/${svgType}_achterkant.svg`
            )
          ).data
          const rightOverviewImage = (
            await DHL_API.get(
              `app/fleet/img/vehicle-damage-location/${svgType}_rechts.svg`
            )
          ).data

          overview = {
            inside: null,
            front: `data:image/svg+xml;base64, ${window.btoa(frontOverviewImage)}`,
            left: `data:image/svg+xml;base64, ${window.btoa(leftOverviewImage)}`,
            rear: `data:image/svg+xml;base64, ${window.btoa(rearOverviewImage)}`,
            right: `data:image/svg+xml;base64, ${window.btoa(rightOverviewImage)}`,
          }

          setOverviewImages(overview)
        } else {
          overview = overviewImages
        }

        setFullPhotos(vehicleFullPhotos)

        const newVehicleData = {
          // For inside photo, we use the default placeholder
          0: {
            ...vehicleData[0],
            fullPhotos: vehicleFullPhotos?.photoInside,
            placeholder: overview?.inside,
          },
          1: {
            ...vehicleData[1],
            fullPhotos: vehicleFullPhotos?.photoFront,
            placeholder: overview?.front,
          },
          2: {
            ...vehicleData[2],
            fullPhotos: vehicleFullPhotos?.photoLeft,
            placeholder: overview?.left,
          },
          3: {
            ...vehicleData[3],
            fullPhotos: vehicleFullPhotos?.photoRear,
            placeholder: overview?.rear,
          },
          4: {
            ...vehicleData[4],
            fullPhotos: vehicleFullPhotos?.photoRight,
            placeholder: overview?.right,
          },
        }

        setVehicleData(newVehicleData)

        const groupedDamages: DamagesBySide = newDamages.reduce(
          (acc: DamagesBySide, damage) => {
            const side: keyof DamagesBySide = (
              ['front', 'left', 'rear', 'right', 'inside'].includes(damage.side)
                ? damage.side
                : 'front'
            ) as keyof DamagesBySide
            if (!acc[side]) {
              acc[side] = []
            }
            acc[side].push(damage)
            return acc
          },
          {
            front: [],
            left: [],
            rear: [],
            right: [],
            inside: [],
          }
        )

        setDamages(groupedDamages)
      },
      onError: (err) => {
        if (
          isWebView === 'true' &&
          window.Android &&
          err?.response.status !== 404
        ) {
          window.Android.failVehicleDamageRegistrationApp()
          customTab === 'true' && window.close()
        }
      },
    }
  )

  return {
    damages,
    photos,
    fullPhotos,
    vehicleData,
    isLoading: isFetching,
    fetchData,
    isError,
  }
}
